import React, { useState, useEffect, useCallback } from 'react';
import Map, { Marker, NavigationControl } from 'react-map-gl';
import { MAPBOX_TOKEN, MAP_STYLE, DEFAULT_ZOOM, DEFAULT_CENTER, MARKER_COLOR } from '../../config/mapbox';
import { FaMapMarkerAlt, FaSearch } from 'react-icons/fa';
import 'mapbox-gl/dist/mapbox-gl.css';

const LocationPicker = ({ initialLocation, onLocationChange, address, onAddressChange }) => {
  const [viewState, setViewState] = useState({
    longitude: initialLocation?.longitude || DEFAULT_CENTER[0],
    latitude: initialLocation?.latitude || DEFAULT_CENTER[1],
    zoom: DEFAULT_ZOOM
  });
  const [markerPosition, setMarkerPosition] = useState(initialLocation || null);
  const [searchQuery, setSearchQuery] = useState(address || '');
  const [isSearching, setIsSearching] = useState(false);

  // Update marker when initial location changes
  useEffect(() => {
    if (initialLocation && initialLocation.latitude && initialLocation.longitude) {
      setMarkerPosition(initialLocation);
      setViewState(prev => ({
        ...prev,
        longitude: initialLocation.longitude,
        latitude: initialLocation.latitude
      }));
    }
  }, [initialLocation]);

  // Geocode address using MapBox Geocoding API
  const geocodeAddress = async (query) => {
    if (!query.trim()) return;
    
    setIsSearching(true);
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_TOKEN}&limit=1`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const [longitude, latitude] = data.features[0].center;
        const newLocation = { latitude, longitude };
        
        setMarkerPosition(newLocation);
        setViewState({
          longitude,
          latitude,
          zoom: 15
        });
        
        if (onLocationChange) {
          onLocationChange(newLocation);
        }
        if (onAddressChange && data.features[0].place_name) {
          onAddressChange(data.features[0].place_name);
          setSearchQuery(data.features[0].place_name);
        }
      } else {
        alert('Address not found. Please try a different search.');
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      alert('Error searching for address. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  // Reverse geocode coordinates to get address
  const reverseGeocode = async (longitude, latitude) => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAPBOX_TOKEN}&limit=1`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const address = data.features[0].place_name;
        if (onAddressChange) {
          onAddressChange(address);
          setSearchQuery(address);
        }
      }
    } catch (error) {
      console.error('Reverse geocoding error:', error);
    }
  };

  const handleMapClick = useCallback((event) => {
    const { lng, lat } = event.lngLat;
    const newLocation = { latitude: lat, longitude: lng };
    
    setMarkerPosition(newLocation);
    if (onLocationChange) {
      onLocationChange(newLocation);
    }
    
    // Get address for the clicked location
    reverseGeocode(lng, lat);
  }, [onLocationChange]);

  const handleSearch = (e) => {
    e.preventDefault();
    e.stopPropagation();
    geocodeAddress(searchQuery);
  };

  const handleSearchClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    geocodeAddress(searchQuery);
  };

  return (
    <div className="location-picker">
      <div className="location-search" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Enter address or location"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                geocodeAddress(searchQuery);
              }
            }}
            style={{
              flex: 1,
              padding: '0.75rem',
              borderRadius: '4px',
              border: '1px solid #ddd',
              fontSize: '1rem'
            }}
          />
          <button
            type="button"
            onClick={handleSearchClick}
            disabled={isSearching}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: MARKER_COLOR,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '1rem',
              fontWeight: '500'
            }}
          >
            <FaSearch />
            {isSearching ? 'Searching...' : 'Search'}
          </button>
        </div>
        <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
          Or click on the map to drop a pin at your location
        </p>
      </div>

      <div style={{ height: '400px', borderRadius: '8px', overflow: 'hidden' }}>
        <Map
          {...viewState}
          onMove={evt => setViewState(evt.viewState)}
          mapboxAccessToken={MAPBOX_TOKEN}
          style={{ width: '100%', height: '100%' }}
          mapStyle={MAP_STYLE}
          onClick={handleMapClick}
          cursor="crosshair"
        >
          <NavigationControl position="top-right" />
          
          {markerPosition && (
            <Marker
              longitude={markerPosition.longitude}
              latitude={markerPosition.latitude}
              anchor="bottom"
              draggable
              onDragEnd={(e) => {
                const { lng, lat } = e.lngLat;
                const newLocation = { latitude: lat, longitude: lng };
                setMarkerPosition(newLocation);
                if (onLocationChange) {
                  onLocationChange(newLocation);
                }
                reverseGeocode(lng, lat);
              }}
            >
              <FaMapMarkerAlt
                size={36}
                color={MARKER_COLOR}
                style={{
                  cursor: 'pointer',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                }}
              />
            </Marker>
          )}
        </Map>
      </div>
    </div>
  );
};

export default LocationPicker; 